// Pen and highlighter: pressure-sensitive ink, hold-to-snap shapes, scribble-to-erase.
import type { InkElement, PenKind, SepiaElement, ShapeElement } from "../../../shared/model";
import type { Editor, PointerInfo, Tool, ToolId } from "../editor";
import { hitTest, inkWorldPoints, newId, newSeed, round2 } from "../elements";
import { type Vec, boxFromPoints, dist, pathLength, segmentsIntersect, simplifyIndices } from "../geometry";
import { inkPath, penOpacity, PENS } from "../ink";
import { recognize, resample, type Recognized } from "../recognize";

const HOLD_MS = 520;

/** A scribble sweeps back and forth across its whole width (or height), five times or more. Quick handwriting turns
 *  back too, in loops and joins, but only a little way, so only sweeps over half the extent count. */
export function isScribble(world: Vec[]): boolean {
  if (world.length < 2) return false;
  const box = boxFromPoints(world);
  const horizontal = box.w >= box.h;
  const sweep = (horizontal ? box.w : box.h) * 0.5;
  if (sweep <= 0) return false;
  let reversals = 0;
  let dir = 0;
  let lo = horizontal ? world[0].x : world[0].y;
  let hi = lo;
  for (let i = 1; i < world.length; i++) {
    const v = horizontal ? world[i].x : world[i].y;
    lo = Math.min(lo, v);
    hi = Math.max(hi, v);
    // turned back by a full sweep since the last turn
    if (dir >= 0 && hi - v >= sweep) {
      if (dir > 0) reversals++;
      dir = -1;
      lo = hi = v;
    } else if (dir <= 0 && v - lo >= sweep) {
      if (dir < 0) reversals++;
      dir = 1;
      lo = hi = v;
    }
  }
  return reversals >= 4;
}

/** How much of a stroke's length lies inside a box (its points alone won't do: a straight line keeps only its ends). */
function shareInside(pts: Vec[], b: { x: number; y: number; w: number; h: number }): number {
  const inBox = (x: number, y: number) => x >= b.x && x <= b.x + b.w && y >= b.y && y <= b.y + b.h;
  if (pts.length < 2) return pts.length && inBox(pts[0].x, pts[0].y) ? 1 : 0;
  let inside = 0;
  let total = 0;
  for (let i = 1; i < pts.length; i++) {
    const a = pts[i - 1];
    const c = pts[i];
    const len = dist(a, c);
    total += len;
    for (let k = 0; k < 16; k++) {
      const t = (k + 0.5) / 16;
      if (inBox(a.x + (c.x - a.x) * t, a.y + (c.y - a.y) * t)) inside += len / 16;
    }
  }
  return total > 0 ? inside / total : inBox(pts[0].x, pts[0].y) ? 1 : 0;
}

export class PenTool implements Tool {
  private pts: number[] = [];
  private sim = false;
  private active = false;
  private holdTimer: number | null = null;
  private holdAnchor: Vec | null = null;
  private snapped: { rec: Recognized; preview: SepiaElement | number[]; box0?: { x: number; y: number; w: number; h: number }; d0?: number } | null = null;
  private lastWorld: Vec | null = null;

  constructor(private editor: Editor, readonly id: ToolId = "pen") {}

  private get penKind(): PenKind {
    return this.id === "highlighter" ? "highlighter" : this.editor.style.pen === "highlighter" ? "fountain" : this.editor.style.pen;
  }

  private get color(): string {
    return this.id === "highlighter" ? this.editor.style.hlColor : this.editor.style.inkColor;
  }

  private get size(): number {
    return this.editor.style.penSize[this.penKind] ?? PENS[this.penKind].size;
  }

  cursor(): string {
    return "crosshair";
  }

  private pressure(p: PointerInfo): number {
    return p.hasPressure ? Math.max(0.02, Math.min(1, p.pressure)) : 0.5;
  }

  onDown(p: PointerInfo): void {
    const ed = this.editor;
    ed.stopEditing();
    if (ed.ui.selection.length) ed.clearSelection();
    if (!ed.canDrawOnActiveLayer()) return;
    if (!ed.isOnPaper(p.world)) return ed.deskHint();
    this.active = true;
    this.snapped = null;
    this.sim = !p.hasPressure;
    this.pts = [p.world.x, p.world.y, this.pressure(p)];
    const path = ed.livePath;
    if (path) {
      path.setAttribute("fill", this.color);
      path.setAttribute("opacity", String(penOpacity(this.penKind)));
      path.style.mixBlendMode = PENS[this.penKind].blend ? "multiply" : "normal";
    }
    ed.setOverlay({ hoverPen: null, snapPreview: null });
    this.render([]);
    this.armHold(p.screen);
  }

  onMove(p: PointerInfo, coalesced: PointerInfo[], predicted: PointerInfo[]): void {
    if (!this.active) return;
    this.lastWorld = p.world;
    const z = this.editor.camera.z;
    if (this.snapped) {
      // after snapping, keep following the pen so the shape can be sized before lifting
      this.updateSnapped(p.world);
      return;
    }
    for (const c of coalesced) {
      const n = this.pts.length;
      const lx = this.pts[n - 3];
      const ly = this.pts[n - 2];
      if (Math.hypot(c.world.x - lx, c.world.y - ly) * z < 0.4) {
        // same spot: keep the firmer pressure
        this.pts[n - 1] = Math.max(this.pts[n - 1], this.pressure(c));
        continue;
      }
      this.pts.push(c.world.x, c.world.y, this.pressure(c));
    }
    this.render(predicted);
    if (this.holdAnchor && dist(this.holdAnchor, p.screen) > 5) this.armHold(p.screen);
  }

  onUp(): void {
    if (!this.active) return;
    this.active = false;
    this.disarmHold();
    const ed = this.editor;
    if (this.snapped) {
      this.commitSnapped();
    } else if (this.pts.length >= 3) {
      const el = this.buildInk(this.pts);
      if (!(ed.settings.scribbleErase && this.id === "pen" && this.tryScribbleErase(el))) ed.insert([el], false);
    }
    this.clearLive();
    ed.history.seal();
  }

  onCancel(): void {
    this.active = false;
    this.disarmHold();
    this.snapped = null;
    this.clearLive();
  }

  onHover(p: PointerInfo | null): void {
    if (!p || p.kind !== "pen") {
      if (this.editor.overlay.hoverPen) this.editor.setOverlay({ hoverPen: null });
      return;
    }
    this.editor.setOverlay({ hoverPen: { p: p.world, r: Math.max(1.5, this.size / 2), color: this.color } });
  }

  deactivate(): void {
    this.onCancel();
    this.editor.setOverlay({ hoverPen: null });
  }

  // ---- rendering the live stroke straight into the DOM ----

  private render(predicted: PointerInfo[]): void {
    const path = this.editor.livePath;
    if (!path) return;
    let pts = this.pts;
    if (predicted.length) {
      pts = pts.slice();
      for (const q of predicted.slice(0, 3)) pts.push(q.world.x, q.world.y, this.pressure(q));
    }
    path.setAttribute("d", inkPath({ pts, size: this.size, pen: this.penKind, sim: this.sim, gamma: this.editor.settings.pressureGamma, live: true }));
  }

  private clearLive(): void {
    const path = this.editor.livePath;
    if (path) path.setAttribute("d", "");
    this.editor.setOverlay({ snapPreview: null });
    this.editor.setTransient(null);
  }

  // ---- building elements ----

  private buildInk(flat: number[], forceUniform = false): InkElement {
    const ed = this.editor;
    const z = ed.camera.z;
    const world: Vec[] = [];
    for (let i = 0; i + 2 < flat.length; i += 3) world.push({ x: flat[i], y: flat[i + 1] });
    // drop redundant samples (well under a screen pixel of error)
    const keep = world.length > 3 ? simplifyIndices(world, 0.18 / z) : world.map((_, i) => i);
    const b = boxFromPoints(world);
    const gamma = ed.settings.pressureGamma;
    const pts: number[] = [];
    for (const i of keep) {
      const pr = forceUniform ? 0.5 : flat[i * 3 + 2];
      pts.push(round2(world[i].x - b.x), round2(world[i].y - b.y), round2(gamma === 1 ? pr : Math.pow(Math.max(0.001, pr), gamma)));
    }
    return {
      id: newId(), type: "ink", x: round2(b.x), y: round2(b.y), w: Math.max(1, round2(b.w)), h: Math.max(1, round2(b.h)), rot: 0, z: "",
      t: Date.now(), pts, color: this.color, size: this.size, pen: this.penKind, sim: forceUniform ? false : this.sim || undefined
    };
  }

  // ---- hold to snap ----

  private armHold(at: Vec): void {
    this.disarmHold();
    if (!this.editor.settings.snapShapes || this.snapped) return;
    this.holdAnchor = at;
    this.holdTimer = window.setTimeout(() => this.trySnap(), HOLD_MS);
  }

  private disarmHold(): void {
    if (this.holdTimer !== null) clearTimeout(this.holdTimer);
    this.holdTimer = null;
    this.holdAnchor = null;
  }

  private trySnap(): void {
    this.holdTimer = null;
    if (!this.active || this.pts.length < 15) return;
    const world: Vec[] = [];
    for (let i = 0; i + 2 < this.pts.length; i += 3) world.push({ x: this.pts[i], y: this.pts[i + 1] });
    if (pathLength(world) * this.editor.camera.z < 30) return;
    const rec = recognize(world);
    if (!rec) return;
    this.snapped = { rec, preview: [] };
    if (rec.kind === "shape") {
      const c = { x: rec.box.x + rec.box.w / 2, y: rec.box.y + rec.box.h / 2 };
      const pen = this.lastWorld ?? world[world.length - 1];
      this.snapped.box0 = { ...rec.box };
      this.snapped.d0 = Math.max(1e-3, dist(pen, c));
    }
    this.showSnapped();
    if (navigator.vibrate) navigator.vibrate(8);
  }

  private snappedInk(rec: Recognized): number[] {
    const spacing = 3 / this.editor.camera.z;
    let line: Vec[];
    let closed = false;
    if (rec.kind === "line") line = [rec.a, rec.b];
    else if (rec.kind === "polyline") line = rec.pts;
    else if (rec.kind === "polygon") {
      line = rec.pts;
      closed = true;
    } else return [];
    const pts: number[] = [];
    for (const p of resample(line, spacing, closed)) pts.push(p.x, p.y, 0.5);
    return pts;
  }

  private snappedShape(rec: Extract<Recognized, { kind: "shape" }>): ShapeElement {
    const st = this.editor.style;
    return {
      id: newId(), type: "shape", kind: rec.shape === "ellipse" ? "ellipse" : rec.shape, x: rec.box.x, y: rec.box.y, w: Math.max(8, rec.box.w), h: Math.max(8, rec.box.h),
      rot: 0, z: "", t: Date.now(), stroke: this.color, fill: st.fill, fillStyle: "none", strokeWidth: Math.max(1.4, this.size * 0.75),
      dash: "solid", rough: Math.min(st.rough, 0.45), seed: newSeed(), text: "", font: st.font, fontSize: 24, align: "center", textColor: this.color
    };
  }

  private showSnapped(): void {
    const snap = this.snapped!;
    const path = this.editor.livePath;
    if (snap.rec.kind === "shape") {
      const shape = this.snappedShape(snap.rec);
      snap.preview = shape;
      if (path) path.setAttribute("d", "");
      this.editor.setOverlay({ snapPreview: shape.id });
      this.editor.setTransient([{ ...shape, z: "~preview" }]);
    } else {
      const pts = this.snappedInk(snap.rec);
      snap.preview = pts;
      if (path) path.setAttribute("d", inkPath({ pts, size: this.size, pen: this.penKind, sim: false, live: false }));
    }
  }

  private updateSnapped(p: Vec): void {
    const snap = this.snapped!;
    const rec = snap.rec;
    if (rec.kind === "line") rec.b = p;
    else if (rec.kind === "shape" && snap.box0 && snap.d0) {
      // keep holding and move the pen to grow or shrink the snapped shape about its centre
      const b0 = snap.box0;
      const c = { x: b0.x + b0.w / 2, y: b0.y + b0.h / 2 };
      const k = Math.max(0.1, dist(p, c) / snap.d0);
      rec.box = { x: c.x - (b0.w * k) / 2, y: c.y - (b0.h * k) / 2, w: b0.w * k, h: b0.h * k };
    } else return;
    this.showSnapped();
  }

  private commitSnapped(): void {
    const snap = this.snapped!;
    const ed = this.editor;
    if (snap.rec.kind === "shape" && !Array.isArray(snap.preview)) {
      ed.insert([{ ...(snap.preview as ShapeElement), t: Date.now() }], false);
    } else if (Array.isArray(snap.preview) && snap.preview.length >= 3) {
      ed.insert([this.buildInk(snap.preview, true)], false);
    }
    this.snapped = null;
  }

  // ---- scribble to erase ----

  private tryScribbleErase(stroke: InkElement): boolean {
    const ed = this.editor;
    const world = inkWorldPoints(stroke);
    if (world.length < 12) return false;
    const box = boxFromPoints(world);
    const diag = Math.hypot(box.w, box.h);
    const total = pathLength(world);
    if (diag * ed.camera.z < 14 || total < diag * 4) return false;
    if (!isScribble(world)) return false;
    // strokes the scribble crosses at least twice, or that sit inside it
    const victims: string[] = [];
    for (const el of ed.doc.all()) {
      if (el.type !== "ink" || !ed.isErasable(el)) continue;
      const pts = inkWorldPoints(el);
      const eb = boxFromPoints(pts);
      if (eb.x > box.x + box.w || eb.x + eb.w < box.x || eb.y > box.y + box.h || eb.y + eb.h < box.y) continue;
      let crossings = 0;
      for (let i = 1; i < pts.length && crossings < 2; i++) {
        for (let j = 1; j < world.length; j++) {
          if (segmentsIntersect(pts[i - 1], pts[i], world[j - 1], world[j])) {
            crossings++;
            if (crossings >= 2) break;
          }
        }
      }
      const inside = eb.x >= box.x && eb.y >= box.y && eb.x + eb.w <= box.x + box.w && eb.y + eb.h <= box.y + box.h && pts.some((q) => hitTest(stroke, q, 6 / ed.camera.z));
      // a long stroke the scribble only clips is not what it meant to erase
      const covered = shareInside(pts, box) >= 0.4;
      if ((crossings >= 2 && covered) || inside) victims.push(el.id);
    }
    if (!victims.length) return false;
    ed.commit({ dels: victims });
    ed.host.toast(`Scribbled away ${victims.length} stroke${victims.length === 1 ? "" : "s"} · undo brings ${victims.length === 1 ? "it" : "them"} back`);
    return true;
  }
}
